import React from 'react'

import {
  APT_CATEGORIES,
  addAptCategory,
  fetchAptCategories
} from 'api/apartmentProcurement'

// At Apartment budget categories, shared by every Category dropdown on the page
// (2026-09-17). The six built-in heads used to be the whole list; when Music
// launched the team needed to add a category without a deploy. The list is
// fetched once per page load and a category added from any dropdown shows up
// in all of them straight away.

export const AGE_GROUPS = ['3-5', '5+']

const builtIns = () => {
  // Tests mock the API module with only the exports they use; a missing one
  // throws on access, so the store must never take the page down with it.
  try {
    return APT_CATEGORIES || []
  } catch (e) {
    return []
  }
}

const ageOf = (name) => String(name || '').split(' ')[0]

// Built-in heads keep their order; an added category goes after the last one
// of its age group, so "5+ Music" sits with the other 5+ heads.
const ordered = (list) => {
  const out = []
  list.forEach((name) => {
    if (!name || out.includes(name)) return
    const last = out.map(ageOf).lastIndexOf(ageOf(name))
    if (last === -1) out.push(name)
    else out.splice(last + 1, 0, name)
  })
  return out
}

let names = ordered(builtIns())
let loading = null
const listeners = new Set()

const publish = (list) => {
  names = ordered(list)
  listeners.forEach((fn) => fn(names))
}

/** Every category name, for code that reads the list outside a render. */
export const aptCategoryNames = () => names

/**
 * The category a picked catalogue item puts on a Requests line, or '' when it
 * is not one the grid offers. Matched against the At Apartment heads ("5+
 * Robotics") — NOT the HQ daily sheet's At Apartment list ("5-12 Robotics"), which is
 * what the pick used, so every 5+ item landed with a blank category (Satya,
 * 2026-09-17: "why is category not populating on its own").
 */
export const matchAptCategory = (value) => {
  const wanted = String(value || '').trim().toLowerCase()
  if (!wanted) return ''
  return names.find((name) => name.toLowerCase() === wanted) || ''
}

/** Only the categories added from Alfred (not the six built-in heads). */
export const addedAptCategoryNames = () =>
  names.filter((name) => !builtIns().includes(name))

export const loadAptCategories = () => {
  if (!loading) {
    loading = Promise.resolve()
      .then(() => fetchAptCategories())
      .then((rows) => {
        publish([...builtIns(), ...(rows || []).map((r) => r.name)])
        return names
      })
      .catch(() => {
        loading = null
        return names
      })
  }
  return loading
}

/** Add a category; resolves to the saved row ({name, already_existed, …}). */
export const createAptCategory = async (ageGroup, subject) => {
  const row = await addAptCategory({ age_group: ageGroup, subject })
  publish([...names, row.name])
  return row
}

/** The live category list; re-renders when one is added. */
export const useAptCategories = () => {
  const [list, setList] = React.useState(names)
  React.useEffect(() => {
    listeners.add(setList)
    setList(names)
    loadAptCategories()
    return () => {
      listeners.delete(setList)
    }
  }, [])
  return list
}
